import { useEffect, useRef, useState } from 'react';
import { Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib';
import { useIdleTimeout } from './useIdleTimeout';
import { SESSION_CONFIG } from '@/lib/constants';
import { isPinSwitchDeviceEnabled, listVaultEntries, lockDevice } from '@/lib/session-vault';

import { logger, setUserContext, clearUserContext } from '@/lib';
export function useAuth() {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // Tracks the last-seen authenticated user id so we can detect an identity
  // CHANGE (e.g. a PIN fast-switch performed in another tab, which the Supabase
  // SDK syncs into this tab's session) and drop the now-stale React Query cache.
  const prevUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          // Only log in development to avoid exposing sensitive info in production
          if (process.env.NODE_ENV === 'development') {
            logger.error('Error getting initial session:', sessionError);
          }
          setError(new Error('Failed to initialize authentication'));
          return;
        }

        setSession(session);
        prevUserIdRef.current = session?.user?.id ?? null;

        // Set Sentry user context for production error tracking
        if (session?.user) {
          setUserContext({
            id: session.user.id,
            email: session.user.email || undefined
          });
        } else {
          clearUserContext();
        }
      } catch (err) {
        // Only log in development to avoid exposing sensitive info in production
        if (process.env.NODE_ENV === 'development') {
          logger.error('Unexpected error during auth initialization:', err);
        }
        setError(err instanceof Error ? err : new Error('Authentication initialization failed'));
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Subscribe to auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      try {
        // Only log auth state changes in development to prevent info leakage
        if (process.env.NODE_ENV === 'development') {
          logger.info('Auth state change:', event);
        }

        // Identity changed under us (e.g. a fast-switch in another tab synced
        // here by the SDK) → the cached data belongs to the previous admin, so
        // drop it. Authorization is still re-derived from the live session.
        const newUserId = session?.user?.id ?? null;
        if (prevUserIdRef.current && newUserId && prevUserIdRef.current !== newUserId) {
          queryClient.clear();
        }
        prevUserIdRef.current = newUserId;

        setSession(session);

        // Set Sentry user context for production error tracking
        if (session?.user) {
          setUserContext({
            id: session.user.id,
            email: session.user.email || undefined
          });
        } else {
          clearUserContext();
        }
        setError(null); // Clear any previous errors on successful auth change
      } catch (err) {
        // Only log in development to avoid exposing sensitive info in production
        if (process.env.NODE_ENV === 'development') {
          logger.error('Error handling auth state change:', err);
        }
        setError(err instanceof Error ? err : new Error('Authentication state change failed'));
      } finally {
        setIsLoading(false);
      }
    });

    // Re-validate the session whenever the tab regains focus.  Supabase's
    // built-in auto-refresh is throttled or paused while the tab is hidden,
    // so a user returning after an extended absence may otherwise navigate
    // protected pages with a dead JWT until they trigger a mutation.
    //
    // React Query's refetchOnWindowFocus/refetchOnReconnect are disabled in
    // react-query-client.ts to prevent them from racing this refresh and
    // failing against a dead connection. After a successful refresh we
    // invalidate all queries so they refetch with a healthy JWT.
    const validateOnFocus = async () => {
      if (document.visibilityState !== 'visible') return;
      const { data: cached } = await supabase.auth.getSession();
      if (!cached.session) return; // not signed in – nothing to validate
      const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !refreshed.session) {
        if (process.env.NODE_ENV === 'development') {
          logger.warn('Session refresh failed on tab focus; signing out', {
            error: String(refreshError),
          });
        }
        await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
        return;
      }
      queryClient.invalidateQueries();
    };

    document.addEventListener('visibilitychange', validateOnFocus);

    // Cleanup subscription
    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', validateOnFocus);
    };
  }, [queryClient]);

  const signOut = async () => {
    try {
      // Clear Sentry user context on logout
      clearUserContext();

      // Clear all React Query cache to prevent stale data leaking across users
      queryClient.clear();

      // Stop auto-refresh before signing out to prevent Supabase from
      // attempting to refresh the token after it's been revoked server-side
      supabase.auth.stopAutoRefresh();

      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) {
        // Only log in development to avoid exposing sensitive info in production
        if (process.env.NODE_ENV === 'development') {
          logger.error('Error signing out:', signOutError);
        }
        throw signOutError;
      }
    } catch (err) {
      // Only log in development to avoid exposing sensitive info in production
      if (process.env.NODE_ENV === 'development') {
        logger.error('Unexpected error during sign out:', err);
      }
      throw err instanceof Error ? err : new Error('Sign out failed');
    }
  };

  // Idle timeout. On a PIN-fast-switch device with saved sessions, LOCK to the
  // switch screen (keep the session) instead of fully signing out — so the next
  // person re-enters with a PIN rather than a password. Otherwise sign out as
  // before. The full-page redirect lands on /switch (the active session is still
  // valid, so StandaloneProtectedRoute lets it render).
  const handleIdleTimeout = () => {
    if (isPinSwitchDeviceEnabled() && listVaultEntries().length > 0) {
      lockDevice(); // real lock — ProtectedRoute now refuses app routes
      if (window.location.pathname !== '/switch') {
        window.location.assign('/switch');
      }
      return;
    }
    void signOut();
  };
  useIdleTimeout({
    timeout: SESSION_CONFIG.IDLE_TIMEOUT_MS,
    onTimeout: handleIdleTimeout,
    enabled: !!session, // Only active when user is authenticated
    throttle: SESSION_CONFIG.ACTIVITY_THROTTLE_MS,
  });

  return {
    session,
    isLoading,
    error,
    signOut,
  };
}